import type {
  ReportCatalog,
  ReportQuery,
  ReportRecordsRequest
} from "@gettactic/api";

/** The dataset whose rows a measure is counted from. */
const DATASET_FOR_MEASURE: Record<string, string> = {
  reservations: "reservations",
  occupancy: "reservations",
  checkin_rate: "reservations",
  no_shows: "reservations",
  room_hours: "reservations",
  participants: "reservations",
  visits: "visits",
  invitations: "visits",
  deliveries: "deliveries",
  new_users: "users"
};

/**
 * Filters a measure implies on its rows, beyond the query's own. A missed
 * check-in is a reservation the office revoked; the "Cancelled for" column
 * says why. An invitation is a visit registered ahead with a code.
 */
const IMPLIED_FILTERS: Record<string, ReportRecordsRequest["filters"]> = {
  room_hours: [{ dimension: "resource_type", in: ["meeting_room"] }],
  no_shows: [{ dimension: "slot_status", in: ["revoked"] }],
  invitations: [{ dimension: "visit_kind", in: ["invited"] }]
};

/**
 * From a number to the rows behind it: the dataset a measure counts, with
 * the query's filters that the dataset can take, over the same period.
 * Null when there is no row-level dataset for the measure (requests).
 */
export const recordsForQuery = (
  catalog: ReportCatalog,
  query: ReportQuery
): ReportRecordsRequest | null => {
  const datasetKey = DATASET_FOR_MEASURE[query.measure];
  const dataset = catalog.datasets.find((d) => d.key === datasetKey);
  if (!dataset) {
    return null;
  }
  const allowed = new Set(dataset.dimensions);
  const own = (query.filters ?? []).filter((f) => allowed.has(f.dimension));
  const implied = (IMPLIED_FILTERS[query.measure] ?? []).filter(
    (f) =>
      allowed.has(f.dimension) && !own.some((o) => o.dimension === f.dimension)
  );
  return {
    dataset: dataset.key,
    filters: [...own, ...implied],
    period: dataset.timeless ? null : query.period
  };
};
